import React, { useEffect, useState } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';

const FreelancerPreferencesPage = () => {
  const [preferences, setPreferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const apiBaseURL = process.env.REACT_APP_API_BASE_URL;

  const columns = [
    'Freelancer ID',
    'Work Category',
    'Preferred Topics',
    'Avoided Topics',
    'Other Topics'
  ];

  useEffect(() => {
    const fetchPreferences = async () => {
      const token = localStorage.getItem('employeeToken');
      if (!token) {
        console.warn("🚫 No employee token found — redirecting to login");
        window.location.href = "/employee-login";
        return;
      }

      try {
        const response = await axios.get(`${apiBaseURL}/api/freelancer-preferences`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPreferences(response.data);
      } catch (err) {
        setError('Failed to fetch freelancer preferences.');
      } finally {
        setLoading(false);
      }
    };

    fetchPreferences();
  }, [apiBaseURL]);

  const data = preferences.map(item => ({
    freelancer_id: item.freelancer_id,
    work_category: item.work_category,
    preferred_topics: item.preferred_topics?.join(', ') || 'None',
    avoided_topics: item.avoided_topics?.join(', ') || 'None',
    other_topics: item.other_topics?.join(', ') || 'None',
  }));

  const handleDownload = () => {
    const now = new Date();
    const formattedDate = now.toISOString().replace(/T/, '_').replace(/:/g, '-').split('.')[0];
    const displayTimestamp = now.toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });

    const headingRows = [
      ['OrderMinds Freelancing Solutions LLP'],
      ['Freelancer Topic Preferences'],
      [`Downloaded on: ${displayTimestamp}`],
      []
    ];

    const headers = columns;
    const rows = data.map(row => [
      row.freelancer_id,
      row.work_category,
      row.preferred_topics,
      row.avoided_topics,
      row.other_topics
    ]);

    const ws = XLSX.utils.aoa_to_sheet([...headingRows, headers, ...rows]);

    // Auto column width
    const maxColWidths = headers.map((_, colIndex) =>
      Math.max(...[...rows.map(row => String(row[colIndex] || '')).map(val => val.length), headers[colIndex].length])
    );
    ws['!cols'] = maxColWidths.map(w => ({ wch: w + 2 }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Freelancer Preferences');

    const filename = `Freelancer_Preferences_${formattedDate}.xlsx`;
    XLSX.writeFile(wb, filename);
  };

  const Table = ({ columns, data }) => {
    return (
      <div className="overflow-x-auto">
        <table className="min-w-full table-auto bg-white shadow-md rounded-lg">
          <thead>
            <tr className="bg-gray-200">
              {columns.map((col, index) => (
                <th key={index} className="px-4 py-2 text-left">{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-2 text-center text-gray-500">No data available</td>
              </tr>
            ) : (
              data.map((row, rowIndex) => (
                <tr key={rowIndex} className="border-b">
                  {columns.map((col, colIndex) => (
                    <td key={colIndex} className="px-4 py-2">
                      {row[col.toLowerCase().replace(/ /g, '_')]}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    );
  };

  if (loading) return <div>Loading freelancer preferences...</div>;
  if (error) return <div>{error}</div>;

  return (
    <div className="flex-1 p-6 bg-gray-100 ml-64">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-semibold">Freelancer Preferences</h2>
        <button
          onClick={handleDownload}
          className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition"
        >
          Download Excel
        </button>
      </div>
      <Table columns={columns} data={data} />
    </div>
  );
};

export default FreelancerPreferencesPage;
